import React, { useCallback, useState } from 'react';

import { FileMimeTypes } from '../../../../../@types/uploaded-files.type';
import { getUploadedFilePresignedUrlByKey } from '../../../../../api/uploadedFiles';
import {
  ImageViewer,
  ViewerImage,
} from '../../../../../components/organisms/AttachmentsPanel/ImageViewer';
import { useJobImageAttachments } from '../../../../../components/organisms/InteractionAnswers';
import { isSafeUrl } from '../../../../../utils/common';
import { downloadFile } from '../../../../../utils/file-utils';
import { getFileAttachmentURLData, isSerialisedPartActionLog } from '../../../../../utils/services';

type LogRecord = Record<string, unknown>;

const IMAGE_MIME_TYPES = new Set<string>([
  FileMimeTypes.PNG,
  FileMimeTypes.JPG,
  FileMimeTypes.JPEG,
]);

/**
 * Log types whose note text embeds a clickable file reference (`<span fileKey=…>`):
 * file uploads, interaction events, and serialised part actions. Detection is by
 * logTypeCode so it doesn't depend on the display-name string.
 */
export const isFileAttachmentLog = (logTypeCode: unknown): boolean => {
  const code = String(logTypeCode ?? '').toLowerCase();
  return code === 'uploads' || code === 'interaction-events' || isSerialisedPartActionLog(code);
};

/**
 * Restores the legacy "click a file in a job note to view/download it" behaviour that
 * the History & Notes rewrite dropped (UI-96). Parses the `fileKey` from the raw note
 * HTML, fetches a presigned URL, and opens images in the shared ImageViewer lightbox
 * (other file types download). Mirrors the working flow in TrackingHistory. Bare URLs
 * are intentionally NOT auto-linked — only these explicit file references are handled.
 */
export const useLogFileViewer = (serviceId?: number) => {
  const [viewing, setViewing] = useState<{ images: ViewerImage[]; index: number } | null>(null);
  const { images: jobImages, indexOfKey } = useJobImageAttachments(serviceId);

  const openFromLog = useCallback(
    async (log: LogRecord, clickedOuterHtml: string) => {
      const logTypeCode = String(log.logTypeCode ?? '');
      if (!isFileAttachmentLog(logTypeCode)) return;

      // Parse from the RAW note (not the sanitised DOM, which strips the custom
      // fileKey attribute). getFileAttachmentURLData returns undefined when the note
      // carries no file reference, so non-file clicks are a no-op.
      const logTypeName = String(log.logTypeName ?? '');
      const data = getFileAttachmentURLData(
        String(log.notes ?? ''),
        logTypeName,
        logTypeCode,
        clickedOuterHtml
      );
      if (!data?.key) return;

      // The clicked file is one of the job's attachments, so open the viewer on the
      // whole set rather than on that file alone — the arrows and dots then browse
      // every photo on the job, including the ones an interaction uploaded.
      const indexInJob = indexOfKey(data.key);
      if (indexInJob >= 0) {
        setViewing({ images: jobImages, index: indexInJob });
        return;
      }

      try {
        const res = await getUploadedFilePresignedUrlByKey(
          data.key,
          data.mimeType,
          data.contentDispositionType
        );
        if (!res?.url || !isSafeUrl(res.url)) return;
        if (data.mimeType && IMAGE_MIME_TYPES.has(data.mimeType)) {
          // Not in the attachments list (a different entity, or the list hasn't
          // loaded); still viewable, just on its own.
          setViewing({ images: [{ url: res.url, fileName: data.fileName }], index: 0 });
        } else {
          // getFileAttachmentURLData strips the extension from `fileName`; recover the
          // full original name (with extension) from the storage key so the downloaded
          // file keeps its type. Key shape: "service/attachments/<ts> -<filename>".
          const sep = data.key.indexOf(' -');
          const downloadName = sep >= 0 ? data.key.slice(sep + 2).trim() : data.fileName;
          downloadFile(res.url, downloadName);
        }
      } catch {
        // File may no longer exist / fetch failed — fail silently, as the mobile flow does.
      }
    },
    [indexOfKey, jobImages]
  );

  const viewer = viewing ? (
    <ImageViewer
      images={viewing.images}
      initialIndex={viewing.index}
      onClose={() => setViewing(null)}
    />
  ) : null;

  return { openFromLog, viewer };
};
